/**
 * Lane D4 view models — Playtests + the six centered modals
 * (INVENTORY-DESKTOP §3.7, §4, §8).
 *
 * The whole playtests surface is already derived: `playtestsVM` (`selectors.ts`)
 * carries the screen, `selectors.3e.ts` carries all five modal bags, and
 * `whatsNewVM` carries the sixth. Lane D4 is a RE-LAYOUT, not a re-derivation —
 * so this file holds exactly one thing: the handful of strings the desktop
 * template spells differently from mobile (§8), expressed the way
 * `selectors.desktop.ts` established in D0-08:
 *
 *   **wrappers, never edits.** `ptDetailVMDesktop` / `ptSyncVMDesktop` /
 *   `ptBrowseVMDesktop` / `ptTrackedVMDesktop` re-map the output of their
 *   `selectors.3e.ts` counterparts. `selectors.3e.ts` keeps its golden-fixture
 *   behaviour, the mobile sheets keep their strings, and the two trees cannot
 *   drift apart on anything but the fields named here.
 *
 * Everything else — `playtestsVM`, `ptSyncChip`, `ptAddVM`, `ptPartyChips`,
 * `whatsNewVM`, `PT_TRACKED_FOOT`, `PT_SYNC_STEP2`, and the compliance block
 * (`PT_SYNC_STEAM_HREF`, `PT_SYNC_ATTR_LINK`, `PT_SYNC_ATTR_TAIL`,
 * `PT_SYNC_PRIVACY`) — applies unchanged.
 *
 * Pure, no closures, explicit `now` wherever time matters (PORTING-NOTES P2-01).
 */

import { ptBrowseVM, ptDetailVM, ptSyncVM, ptTrackedVM, PT_BROWSE_HEAD } from './selectors.3e.ts';
import type { PtBrowseVM, PtDetailVM, PtSyncVM, PtTrackedVM } from './selectors.3e.ts';
import type { QdStore } from './types.ts';

// ---------------------------------------------------------------------------
// 1. screen copy that differs from mobile (§8)
// ---------------------------------------------------------------------------

/** Mobile stops at `UNRELEASED GAMES`; desktop names what it excludes. */
export const PT_SUB_DESKTOP =
  'CLOSED BETAS + PLAYTESTS FOR UNRELEASED GAMES · RELEASED / EARLY-ACCESS TEST BRANCHES EXCLUDED';

/** Mobile's empty state offers only `+ ADD PLAYTEST`; desktop has both doors. */
export const PT_EMPTY_DESKTOP =
  'NOTHING MATCHES — CLEAR FILTERS, OR TRACK A TEST VIA + ADD PLAYTEST / ⊞ BROWSE STEAM';

/**
 * The attribution footer, prototype line 508. Longer than mobile's
 * `screens/Playtests.tsx#PT_DATA_FOOTER` in four places: the `(OPEN / CLOSED)`
 * qualifier, `WINDOWS + WAVES`, `DETECTED FROM YOUR STEAM LIBRARY`, and the
 * closing auto-track sentence.
 *
 * That closing sentence used to read `ARE TRACKED AUTOMATICALLY`, and migration
 * 3005 made it false. The store-page widget is now the test, so an unreleased
 * game on the list is WATCHED and a row arrives only once a signup page is
 * confirmed — the same correction `selectors.3e.ts#PT_TRACKED_FOOT` took, and
 * deliberately in the same words, because two surfaces making one claim should
 * not make it twice over. #55 fixed the tracked-modal half and missed this one;
 * it shipped a promise the trigger stopped keeping (AC-01 supersedes AT-01).
 */
export const PT_DATA_FOOTER_DESKTOP =
  'DATA: STORE-WIDGET SCRAPE NIGHTLY (OPEN / CLOSED) · WINDOWS + WAVES DETECTED FROM PLAYTEST-APP PLAYER COUNTS · GRANTED ACCESS DETECTED FROM YOUR STEAM LIBRARY · REQUESTS SELF-REPORTED · OFF-STEAM ENTRIES USER-ADDED + ATTRIBUTED · UNRELEASED GAMES ON GAMES TONIGHT? ARE WATCHED — THEY APPEAR HERE WHEN A SIGNUP PAGE DOES';

/**
 * The sync chip's `data-tip` (prototype line 424), UPPERCASED at the call site
 * per D0-04 — the prototype authors tooltips in sentence case and
 * `text-transform`s them in CSS, `components/Tooltip.tsx` does not.
 */
export const PT_SYNC_TIP = 'STEAM LINK · LIBRARY CHECKED ON DEMAND + NIGHTLY';

/**
 * The row's window-box border (prototype `winBox`, line 1929-1932) — the one
 * genuinely MISSING field on this lane, not merely a reworded one.
 *
 * `lib/logic.ts#ptWinBox` was ported from the mobile template, which drew no box
 * around the window block and therefore never needed `wBs`/`wBd`; the desktop
 * card's first column is that box. The four states are total and the label is
 * `ptWinBox`'s own discriminator, so this maps them rather than re-deriving
 * `ptGroupOf` from a row that no longer carries its `Playtest`. Anything
 * unrecognised falls back to the calm `NO DATES` treatment.
 */
export interface PtWinBorder {
  /** `border-style` */
  bs: string;
  /** `border-color` */
  bd: string;
}

const WIN_BORDER: Readonly<Record<string, PtWinBorder>> = {
  'LIVE NOW': { bs: 'solid', bd: 'var(--acc)' },
  UPCOMING: { bs: 'solid', bd: 'var(--gt-border-2)' },
  ENDED: { bs: 'dashed', bd: 'var(--gt-border-2)' },
  'NO DATES': { bs: 'dashed', bd: 'var(--gt-border-3)' },
};

export function ptWinBorder(wLbl: string): PtWinBorder {
  return WIN_BORDER[wLbl] ?? { bs: 'dashed', bd: 'var(--gt-border-3)' };
}

// ---------------------------------------------------------------------------
// 2. modal copy that differs from mobile (§8)
// ---------------------------------------------------------------------------

/** The pt-detail person ✕ tooltip (prototype line 849). */
export const PT_CLEAR_TIP = 'MISCLICKED? CLEAR YOUR STATUS';

/** The tracked-row ✕ tooltip (prototype line 1019). */
export const PT_UNTRACK_TIP = 'UNTRACK';

/** What's New header tooltips (prototype lines 1084, 1086). */
export const WN_CLEAR_TIP = 'MARK ALL AS REVIEWED';
export const WN_CLOSE_TIP = 'CLOSE';

/**
 * Add-playtest, off-Steam footer (prototype line 950). Mobile stops at
 * `“NO DATED WINDOW”.`; desktop promises the entry is fixable. Curly quotes are
 * the source's own.
 */
export const PT_ADD_OFF_FOOT_DESKTOP =
  'OFFICIAL PAGES ONLY — LINKS THAT LOOK LIKE CHAT INVITES GET FLAGGED. NO DATES? IT LISTS UNDER “NO DATED WINDOW” UNTIL SOMEONE ADDS THEM.';

/** Sync-modal header note (prototype line 1033); mobile's sheet omits ` ON PLAYTESTS`. */
/** See the twin note in `sheets/PtSyncSheet.tsx` for why `+ “REQUESTED”` left:
 *  this sheet reads a library, and a pending request is not in one. */
export const PT_SYNC_NOTE_DESKTOP = 'POWERS “GOT IN” ON PLAYTESTS — REQUESTS ARE SELF-REPORTED';

/**
 * Browse-modal header (prototype line 962). NOT listed in §8, but the desktop
 * template really does append `, APPID + STORE LINK COME WITH EVERY ROW` to the
 * shared mobile string (prototype line 1156 ends at `NIGHTLY`; the ported
 * `PT_BROWSE_HEAD` tail deliberately diverges — see its comment). Composed from
 * the shared constant so the two can only differ by this suffix — including the
 * `2,143` that §12 item 8 flags as disagreeing with the footer's `212`, which
 * both trees still reproduce verbatim.
 */
export const PT_BROWSE_HEAD_DESKTOP = PT_BROWSE_HEAD + ', APPID + STORE LINK COME WITH EVERY ROW';

/**
 * Sync-modal footer (prototype line 1070). Two additions over `ptSyncVM.foot`:
 * the password clause names Steam's own page, and the off-Steam clause says
 * what to do instead.
 */
function syncFootDesktop(lastSync: string): string {
  return (
    'CHECKED ' +
    lastSync +
    " · UPDATES ON CHECK NOW · NIGHTLY SWEEP ~09:17 UTC AS BACKSTOP · WE NEVER GET YOUR PASSWORD — SIGN-IN IS STEAM'S OWN PAGE · OFF-STEAM TESTS (BUNGIE.NET ETC.) AREN'T COVERED: LINK YOUR ID ON THEIR SIGNUP PAGE, SELF-REPORT ON THE GAME"
  );
}

/**
 * The one clause that differs inside `ptDetailVM.attrTxt` (prototype line 2410
 * vs mobile's 2144). Everything around it — the byline, the date, the
 * `datesSrc` provenance tail — is identical, so this is a surgical swap rather
 * than a second derivation of the same sentence. A no-op if `selectors.3e.ts`
 * ever rewords its half: `String.replace` with a string needle leaves the input
 * untouched when it does not match, so the desktop falls back to the mobile
 * wording instead of rendering a mangled sentence.
 */
const ATTR_CLAUSE_MOBILE = 'STATUS + DATES ARE USER-EDITED; EVERY EDIT IS ATTRIBUTED.';
const ATTR_CLAUSE_DESKTOP = 'STATUS + DATES ARE USER-EDITED; ANYONE CAN UPDATE, EVERY EDIT IS ATTRIBUTED.';

// ---------------------------------------------------------------------------
// 3. the four wrappers
// ---------------------------------------------------------------------------

/** `ptDetailVM` with the desktop attribution clause (§8). */
export function ptDetailVMDesktop(s: QdStore, now: number = Date.now()): PtDetailVM | null {
  const v = ptDetailVM(s, now);
  if (!v) return null;
  return { ...v, attrTxt: v.attrTxt.replace(ATTR_CLAUSE_MOBILE, ATTR_CLAUSE_DESKTOP) };
}

/** `ptSyncVM` with the desktop footer (§8). `lastSync` is untouched freshness. */
export function ptSyncVMDesktop(s: QdStore, now: number = Date.now()): PtSyncVM {
  const v = ptSyncVM(s, now);
  return { ...v, foot: syncFootDesktop(v.lastSync) };
}

/** `ptBrowseVM` with the desktop header. The footer string is identical. */
export function ptBrowseVMDesktop(s: QdStore): PtBrowseVM {
  return { ...ptBrowseVM(s), head: PT_BROWSE_HEAD_DESKTOP };
}

/**
 * `ptTrackedVM` with desktop `✕` sizing. `xFs` is `13px` on mobile because the
 * cell is a 44px tap target; the desktop row's ✕ is `11px` (prototype line
 * 1019). The armed `SURE?` keeps the 8.5px it needs to fit either way, and the
 * arm rule itself is re-read from the store rather than sniffed off `xTxt`:
 * only a crew-wide row arms, an `auto` row is a per-user mute (PORTING-NOTES
 * D-04) and never reads `SURE?`.
 */
export function ptTrackedVMDesktop(s: QdStore, now: number = Date.now()): PtTrackedVM {
  const v = ptTrackedVM(s, now);
  return {
    ...v,
    rows: v.rows.map((r) => ({ ...r, xFs: !r.auto && s.ptRmArm === r.id ? '8.5px' : '11px' })),
  };
}
